import { useCallback, useRef, useSyncExternalStore } from "react";
import { store } from "store/createStore";

const refEqual = (a, b) => a === b;

const shallowEqual = (a, b) => {
  if (a === b) return true;
  // If either value is not an object (primitives, functions) or is null,
  // they can only be equal by reference — which already failed above
  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  ) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (let i = 0; i < keysA.length; i++) {
    const key = keysA[i];
    if (a[key] !== b[key]) return false;
  }

  return true;
};

/**
 * A custom useSelector hook that mirrors the react-redux v8+ useSelector API.
 * Uses React 18's useSyncExternalStore for tear-free concurrent rendering.
 *
 * @param {Function} selector - A function that extracts a value from the Redux store state.
 * @param {Function} [equalityFn] - Optional comparison function (defaults to === reference equality,
 *   matching react-redux behaviour). Pass a deep/shallow equality function when selecting new objects/arrays.
 * @returns {*} The selected value from the store.
 *
 * @example
 * const userId = useSelector(state => state.login.userInfo.id);
 *
 * @example
 * // With a custom equality function for derived objects
 * const filters = useSelector(state => ({ a: state.a, b: state.b }), _.isEqual);
 */
const useSelector = (selector, equalityFn = refEqual) => {
  const prevResultRef = useRef();
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const equalityFnRef = useRef(equalityFn);
  equalityFnRef.current = equalityFn;

  const subscribe = useCallback(onStoreChange => {
    return store.subscribe(onStoreChange);
  }, []);

  const getSnapshot = useCallback(() => {
    const nextResult = selectorRef.current(store.getState());

    if (
      prevResultRef.current !== undefined &&
      equalityFnRef.current(prevResultRef.current, nextResult)
    ) {
      return prevResultRef.current;
    }

    prevResultRef.current = nextResult;
    return nextResult;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot);
};

/**
 * Like useSelector but with shallow equality on the returned object's keys —
 * behaves the same way connect + mapStateToProps compares props.
 * Use when your selector returns a new object each time:
 *
 * @example
 * const { userId, isParent } = useShallowSelector(state => ({
 *   userId: state.login.userInfo.id,
 *   isParent: getIsParent(state),
 * }));
 */
const useShallowSelector = selector => useSelector(selector, shallowEqual);

export default useSelector;
export { useShallowSelector, shallowEqual };
